import React, { useMemo } from 'react';
import { Check, Copy, Image as ImageIcon, Info, Loader2, RefreshCw, Search } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';

const shortId = (id) => (id ? `...${String(id).slice(-8)}` : '-');

const formatDateTime = (dateString) => {
  if (!dateString) return 'Unknown';
  let normalizedDate = dateString;
  if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+')) {
    normalizedDate = `${dateString}Z`;
  }
  return new Date(normalizedDate).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const selectClassName = 'h-9 rounded-md border border-gray-700 bg-gray-950 px-3 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-amber-500';

const getImageId = (image, index) => image.id || image.image_id || image._id || `${image.case_id || 'image'}-${index}`;
const getTargetLabel = (image) => image.target_type || image.subject_type || image.entity_type || '-';
const getTargetId = (image) => image.target_id || image.subject_id || image.entity_id || image.suspect_id || image.location_id || '';
const getImageType = (image) => image.image_type || image.kind || 'unknown';
const getImageUrl = (image) => image.image_url || image.url || image.r2_url || image.public_url || '';
const getPromptDetails = (image) => {
  const enhancedPrompt = image.enhanced_prompt || image.revised_prompt || image.metadata?.enhanced_prompt || '';
  const rawPrompt = image.raw_prompt || image.prompt || image.image_prompt || image.metadata?.raw_prompt || image.metadata?.prompt || '';
  return {
    enhancedPrompt,
    rawPrompt,
    hasBoth: Boolean(enhancedPrompt && rawPrompt && enhancedPrompt !== rawPrompt),
    primaryPrompt: enhancedPrompt || rawPrompt,
  };
};

export const AdminImageAudit = ({
  images = [],
  total = 0,
  loading = false,
  showControls = true,
  filters = {},
  onFiltersChange,
  onRefresh,
  onViewCaseInfo,
  onCopyId,
  copiedId,
}) => {
  const rowStats = useMemo(() => {
    return images.reduce((stats, image) => {
      const success = image.success === true;
      if (success) stats.success += 1;
      if (!success) stats.failed += 1;
      return stats;
    }, { success: 0, failed: 0 });
  }, [images]);

  const updateFilter = (key, value) => {
    onFiltersChange?.({
      ...filters,
      [key]: value,
      offset: 0,
    });
  };

  const clearFilters = () => {
    onFiltersChange?.({
      case_id: '',
      target_type: '',
      image_type: '',
      success: '',
      limit: filters.limit || 100,
      offset: 0,
    });
  };

  const hasFilters = Boolean(filters.case_id || filters.target_type || filters.image_type || filters.success);
  const showRowActions = Boolean(onViewCaseInfo || onCopyId);
  const columnCount = showRowActions ? 7 : 6;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Image Audit</h2>
          <p className="text-sm text-gray-400">
            {loading ? 'Loading image rows...' : `Showing ${images.length} of ${total || images.length} image audit rows`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="bg-emerald-900/50 text-emerald-300 border border-emerald-700">
            Success {rowStats.success}
          </Badge>
          <Badge className={rowStats.failed > 0
            ? 'bg-orange-900/50 text-orange-300 border border-orange-700'
            : 'bg-gray-800 text-gray-400 border border-gray-700'}
          >
            Failed {rowStats.failed}
          </Badge>
          {showControls && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={loading || !onRefresh}
              className="border-gray-700 text-gray-200"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {showControls && (
        <Card className="rounded-lg border-gray-700 bg-gray-900/60 shadow-none">
          <CardContent className="p-3">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex min-w-[220px] flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Case ID
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-600" />
                  <Input
                    value={filters.case_id || ''}
                    onChange={(event) => updateFilter('case_id', event.target.value)}
                    placeholder="case id"
                    className="border-gray-700 bg-gray-950 pl-8 text-gray-200 placeholder:text-gray-600"
                  />
                </div>
              </label>
              <label className="flex min-w-[150px] flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Target
                <select className={selectClassName} value={filters.target_type || ''} onChange={(event) => updateFilter('target_type', event.target.value)}>
                  <option value="">All</option>
                  <option value="suspect">Suspect</option>
                  <option value="victim">Victim</option>
                  <option value="location">Location</option>
                  <option value="evidence">Evidence</option>
                  <option value="guide">Guide</option>
                </select>
              </label>
              <label className="flex min-w-[150px] flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Image Type
                <select className={selectClassName} value={filters.image_type || ''} onChange={(event) => updateFilter('image_type', event.target.value)}>
                  <option value="">All</option>
                  <option value="portrait">Portrait</option>
                  <option value="location">Location</option>
                  <option value="evidence">Evidence</option>
                  <option value="scene">Scene</option>
                </select>
              </label>
              <label className="flex min-w-[140px] flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Success
                <select className={selectClassName} value={filters.success || ''} onChange={(event) => updateFilter('success', event.target.value)}>
                  <option value="">Any</option>
                  <option value="true">Success</option>
                  <option value="false">Failed</option>
                </select>
              </label>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-400 hover:text-white">
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="rounded-lg border border-gray-700 bg-gray-900/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1120px]">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-800/60">
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Image</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Case</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Target</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Status</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Prompt</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Created</th>
                {showRowActions && (
                  <th className="p-3 text-right text-xs font-medium uppercase tracking-wide text-gray-400">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {loading ? (
                <tr>
                  <td colSpan={columnCount} className="p-8 text-center text-gray-400">
                    <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />
                    Loading image audit
                  </td>
                </tr>
              ) : images.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="p-8 text-center text-gray-500">
                    No image audit rows match the current filters.
                  </td>
                </tr>
              ) : images.map((image, index) => {
                const caseId = image.case_id;
                const imageUrl = getImageUrl(image);
                const targetId = getTargetId(image);
                const promptDetails = getPromptDetails(image);
                const success = image.success === true;

                return (
                  <tr key={getImageId(image, index)} className="hover:bg-gray-800/40 transition-colors">
                    <td className="p-3 align-top">
                      <div className="flex items-center gap-3">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt=""
                            className="h-12 w-12 rounded border border-gray-700 object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded border border-gray-700 bg-gray-950">
                            <ImageIcon className="h-5 w-5 text-gray-600" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium capitalize text-white">{getImageType(image).replace(/_/g, ' ')}</div>
                          <div className="text-xs text-gray-500">{image.subject_gender || image.gender || 'gender unknown'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 align-top">
                      {caseId && onCopyId ? (
                        <button
                          type="button"
                          onClick={() => onCopyId(caseId, 'case')}
                          className="flex items-center gap-1 font-mono text-xs text-gray-400 hover:text-gray-200"
                          title={caseId}
                        >
                          {shortId(caseId)}
                          {copiedId === caseId ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                        </button>
                      ) : (
                        <span className="font-mono text-xs text-gray-500" title={caseId || ''}>
                          {shortId(caseId)}
                        </span>
                      )}
                    </td>
                    <td className="p-3 align-top">
                      <div className="text-sm capitalize text-gray-300">{getTargetLabel(image).replace(/_/g, ' ')}</div>
                      <div className="font-mono text-xs text-gray-500" title={targetId}>
                        {shortId(targetId)}
                      </div>
                    </td>
                    <td className="p-3 align-top">
                      <Badge className={success
                        ? 'bg-emerald-900/50 text-emerald-300 border border-emerald-700'
                        : 'bg-orange-900/50 text-orange-300 border border-orange-700'}
                      >
                        {success ? 'success' : 'failed'}
                      </Badge>
                      {!success && image.error && (
                        <p className="mt-1 max-w-[220px] text-xs text-orange-300 line-clamp-2">
                          {image.error}
                        </p>
                      )}
                    </td>
                    <td className="p-3 align-top">
                      {promptDetails.hasBoth ? (
                        <div className="max-w-[360px] space-y-2">
                          <div>
                            <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Enhanced</div>
                            <p className="text-sm text-gray-400 line-clamp-2">{promptDetails.enhancedPrompt}</p>
                          </div>
                          <div>
                            <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Raw</div>
                            <p className="text-xs text-gray-500 line-clamp-2">{promptDetails.rawPrompt}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="max-w-[360px] text-sm text-gray-400 line-clamp-2">
                          {promptDetails.primaryPrompt || 'No prompt stored'}
                        </p>
                      )}
                    </td>
                    <td className="p-3 align-top text-sm text-gray-400">
                      {formatDateTime(image.created_at || image.timestamp)}
                    </td>
                    {showRowActions && (
                      <td className="p-3 align-top">
                        <div className="flex justify-end gap-1">
                          {caseId && onViewCaseInfo && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onViewCaseInfo(caseId, image.session_id)}
                              className="h-8 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
                              title="View case info"
                            >
                              <Info className="h-4 w-4" />
                            </Button>
                          )}
                          {caseId && onCopyId && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onCopyId(caseId, 'case')}
                              className="h-8 px-2 text-gray-400 hover:text-white hover:bg-gray-700"
                              title="Copy case ID"
                            >
                              {copiedId === caseId ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminImageAudit;
